/*
 * File: FancySelect.tsx
 * Project: rosita
 * -----
 * Last Modified: 06 Mar 2022 1:39:19 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Select, SelectProps } from 'antd';
import { AddressLabels } from 'models/Address';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { BaseSelectRef } from 'rc-select';
import { FC, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'redux/store';

interface FancySelectProps extends SelectProps {
  width?: number;
  initialTop?: boolean;
}

const styles: InlineStylesModel = {
  form: {
    position: 'relative',
    borderRadius: 0
  },
  borderAnimation: {
    transition: 'all 0.3s ease'
  },
  input: {
    display: 'inline-block',
    width: '100%'
  },
  labelUnfocused: {
    position: 'absolute',
    color: 'rgba(0,0,0,0.50)',
    top: 5,
    left: 10,
    transition: 'all 0.3s ease'
  },
  labelFocused: {
    backgroundColor: 'white',
    position: 'absolute',
    paddingLeft: 5,
    paddingRight: 5,
    top: -15,
    left: 8,
    transition: 'all 0.3s ease',
    borderRadius: 5
  }
};

export const FancySelect: FC<FancySelectProps> = ({ width, value, placeholder, defaultValue, mode, initialTop, ...rest }: FancySelectProps) => {
  const [isLabelTop, _setIsLabelTop] = useState(initialTop || false);
  const [open, _setOpen] = useState(false);

  const { subdivisionLocalCodeEquals, countryAlpha2CodeEquals } = useAppSelector((state) => state.chuckieSueAddressesParams);
  const inputRef = useRef<BaseSelectRef>(null);

  const inputWidth = width ? { ...styles.input, width } : styles.input;

  const handleFocus = (): void => {
    _setIsLabelTop(true);
    _setOpen(true);
  };

  const onLabelClick = (): void => {
    if (!isLabelTop) {
      _setIsLabelTop(true);
      _setOpen(true);
      inputRef.current?.focus();
    }
  };

  const handleBlur = (): void => {
    if (!value || (Array.isArray(value) && !value.length)) {
      _setIsLabelTop(false);
      _setOpen(false);
    }
    if ((typeof defaultValue !== 'undefined' && !defaultValue) || (Array.isArray(defaultValue) && !defaultValue.values && typeof defaultValue !== 'undefined')) {
      _setIsLabelTop(false);
    }

    _setOpen(false);
  };

  const handleSelect = (): void => {
    setTimeout(() => inputRef.current?.blur(), 100);
  };

  const handleClear = (): void => {
    _setIsLabelTop(false);
    _setOpen(false);
    setTimeout(() => inputRef.current?.blur(), 100);
  };

  useEffect(() => {
    if (subdivisionLocalCodeEquals === undefined && placeholder === AddressLabels.subdivisionLocalCode) _setIsLabelTop(false);
  }, [subdivisionLocalCodeEquals]);

  return (
    <div style={isLabelTop ? { ...styles.form, ...styles.borderAnimation } : styles.form}>
      <Select
        {...rest}
        mode={mode}
        value={value}
        ref={inputRef}
        style={inputWidth}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSelect={handleSelect}
        // onClick={handleClick}
        allowClear
        onClear={handleClear}
        open={open}
        filterOption={(input, option): boolean =>
          !!option &&
          !!option.value &&
          typeof option.value === 'string' &&
          typeof option.label === 'string' &&
          (option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0)
        }></Select>
      <label style={isLabelTop ? styles.labelFocused : styles.labelUnfocused} onClick={onLabelClick}>
        <span>{placeholder}</span>
      </label>
    </div>
  );
};
